// Role utilities for SLYRSA Frontend
// Single source for client-side role/permission checks.
import type { UserProfile } from '@/services/api'
import { RH } from '@/constants/permissions'

type UserData = UserProfile | any

/**
 * Extracts role keys from user data (RBAC-first).
 * @param user - User data object
 * @returns Array of role keys
 */
export const getUserRoles = (user?: UserData): string[] => {
  if (!user) {
    return []
  }
  
  if (user.rbac?.roles?.length) {
    const roleKeys = user.rbac.roles.map((role: any) => role.key)
    return roleKeys
  }

  // Backward compatibility only.
  if (user.roles?.length) {
    const roleKeys = user.roles.map((role: any) => role.key)
    return roleKeys
  }
  
  return []
}

/**
 * Checks if user has any of the specified roles
 * @param user - User data object
 * @param roleKeys - Array of role keys to check
 * @returns boolean indicating if user has any of the roles
 */
export const hasAnyRole = (user?: UserData, roleKeys: string[] = []): boolean => {
  const userRoles = getUserRoles(user)
  const result = roleKeys.some(role => userRoles.includes(role))
  return result
}

export const hasRole = (user?: UserData, roleKey?: string): boolean => {
  if (!roleKey) return false
  return getUserRoles(user).includes(roleKey)
}

/**
 * Checks if a user can perform actions on job requests (approve, reject, etc.)
 * Uses permission-based checks.
 * @param user - User data object
 * @returns boolean indicating if the user can perform job request actions
 */
export const canPerformJobRequestActions = (user?: UserData): boolean => {
  return (
    canPreApproveJobRequests(user) ||
    canAssignJobRequestSalaries(user) ||
    canFinalApproveJobRequests(user)
  )
}

/**
 * Quien ve los botones de pre-aprobar y aprobar definitivamente.
 *
 * Sigue la misma regla que el servidor: el Encargado de área, el maestro, o
 * quien traiga el permiso de aprobar en todas las áreas. Antes esto miraba
 * solo `isAreaManager`, así que quien tenía el permiso podía hacerlo por API
 * pero no veía el botón — y al revés, el servidor dejaba pasar a roles que la
 * pantalla nunca habilitó.
 */
const puedeAprobarSolicitudes = (user?: UserData): boolean =>
  user?.isAreaManager === true ||
  user?.rbac?.isMaster === true ||
  // Los permisos llegan asíncronos: sin el `?? []` la pantalla revienta
  // mientras cargan, en vez de solo no mostrar el botón todavía.
  userHasPermission(
    RH.SOLICITUDES_APROBAR_TODAS_LAS_AREAS,
    user?.rbac?.effectivePermissions ?? [],
  )

export const canPreApproveJobRequests = (user?: UserData): boolean =>
  puedeAprobarSolicitudes(user)

export const canFinalApproveJobRequests = (user?: UserData): boolean =>
  puedeAprobarSolicitudes(user)

/**
 * Quien ve Rechazar y Reactivar (SRH-322): quien puede aprobar —Encargado de
 * área, MASTER o aprobar_todas_las_areas— y, aparte, quien tiene el permiso
 * «Eliminar solicitudes». No hay un permiso propio de rechazar; ese es el que
 * en la pantalla de roles hace de «Rechazar».
 *
 * Tiene que decir lo mismo que el servidor: quien lo ve sin poder usarlo
 * recibe un 403. De qué áreas puede rechazar lo decide el servidor.
 */
export const canRejectJobRequests = (user?: UserData): boolean =>
  puedeAprobarSolicitudes(user) ||
  userHasPermission(
    RH.SOLICITUDES_PERSONAL_DELETE,
    user?.rbac?.effectivePermissions ?? [],
  )

export const canAssignJobRequestSalaries = (user?: UserData): boolean => {
  return user?.canAssignSalaries === true
}

/**
 * Si al usuario se le muestran los montos de compensación (SRH-319).
 *
 * El solicitante levanta la requisición y sigue a sus candidatos, pero la
 * compensación no es asunto suyo: el rango lo asigna RH y lo aprueba el jefe
 * de área.
 *
 * El servidor ya recorta los montos de la respuesta, así que esto no es la
 * defensa —es para no dejar en pantalla la sección vacía, con etiquetas sin
 * valor, que es peor que no mostrarla. Las dos reglas tienen que decir lo
 * mismo: si cambia una, cambia la otra.
 */
export const puedeVerCompensacion = (user?: UserData): boolean =>
  user?.rbac?.isMaster === true ||
  user?.canAssignSalaries === true ||
  user?.isAreaManager === true ||
  [
    RH.GESTION_SOLICITUDES_READ,
    RH.SOLICITUDES_APROBAR_TODAS_LAS_AREAS,
    RH.SOLICITUDES_ASIGNAR_SALARIOS_TODAS_LAS_AREAS,
  ].some((clave) =>
    userHasPermission(clave, user?.rbac?.effectivePermissions ?? []),
  )

/**
 * Checks if a user can conduct interviews (RH or Final)
 * Uses permission-based checks.
 * @param user - User data object
 * @param interviewType - Type of interview ('rh' | 'final')
 * @returns boolean indicating if the user can conduct interviews
 */
export const canConductInterviews = (user?: UserData, interviewType: 'rh' | 'final' = 'rh'): boolean => {
  // Check if user has isMaster flag
  if (user?.rbac?.isMaster === true) {
    return true
  }
  
  if (interviewType === "rh") {
    const permissions = user?.rbac?.effectivePermissions || []
    return (
      userHasPermission("rh.entrevistas.base.create", permissions) ||
      userHasPermission("rh.entrevistas.base.create", permissions)
    )
  }
  
  if (interviewType === 'final') {
    const permissions = user?.rbac?.effectivePermissions || []
    return (
      userHasPermission("rh.entrevistas.base.create", permissions) ||
      userHasPermission("rh.solicitudes_personal.base.create", permissions)
    )
  }
  
  return false
}



/**
 * Gets the appropriate badge color class for a user role
 * @param role - The user's role string
 * @returns CSS class string for role badge styling
 */
export const getRoleBadgeColor = (role: string): string => {
  // Master role - highest priority
  if (role === 'MASTER') {
    return 'bg-purple-50 text-purple-900 border-purple-300'
  }

  // Default for unknown roles
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

export function userHasPermission(
  permissionKey: string,
  userPermissions: string[]
): boolean {
  return userPermissions.includes(permissionKey);
}
